import { lazy, Suspense, useEffect, useRef, type ComponentType, type LazyExoticComponent } from "react";
import FingerprintIQ from "@fingerprintiq/js";
import { Route, Routes, useLocation } from "react-router-dom";
import { useSession } from "./lib/auth-client";
import Header from "./components/Header";
import Footer from "./components/Footer";
import AdminToolbar from "./components/AdminToolbar";
import Loading from "./components/Loading";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { clearCache } from "./lib/api-cache";

const Home = lazy(() => import("./pages/Home"));
const FreeExport = lazy(() => import("./pages/FreeExport"));
const Report = lazy(() => import("./pages/Report"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const Privacy = lazy(() => import("./pages/Privacy"));
const TOS = lazy(() => import("./pages/TOS"));
const Admin = lazy(() => import("./pages/Admin"));
const Support = lazy(() => import("./pages/Support"));
const StoreDirectory = lazy(() => import("./pages/StoreDirectory"));
const StorePage = lazy(() => import("./pages/StorePage"));
const PaymentComplete = lazy(() => import("./pages/PaymentComplete"));
const StoreDetail = lazy(() => import("./pages/StoreDetail"));
const StoreCompare = lazy(() => import("./pages/StoreCompare"));
const DocsLayout = lazy(() => import("./pages/docs/DocsLayout"));
const DocsOverview = lazy(() => import("./pages/docs/Overview"));
const DocsHowItWorks = lazy(() => import("./pages/docs/HowItWorks"));
const DocsStoreReports = lazy(() => import("./pages/docs/StoreReports"));
const DocsProductExportPage = lazy(() => import("./pages/docs/ProductExport"));
const DocsDownloads = lazy(() => import("./pages/docs/Downloads"));
const DocsAppsThemes = lazy(() => import("./pages/docs/AppsThemes"));
const DocsPageSpeed = lazy(() => import("./pages/docs/PageSpeed"));
const DocsMonitoring = lazy(() => import("./pages/docs/Monitoring"));
const DocsFreeExport = lazy(() => import("./pages/docs/FreeExport"));
const DocsBuyingReport = lazy(() => import("./pages/docs/BuyingReport"));
const DocsAuthentication = lazy(() => import("./pages/docs/Authentication"));
const DocsApiReference = lazy(() => import("./pages/docs/ApiReference"));
const DocsX402Payment = lazy(() => import("./pages/docs/X402Payment"));
const DocsWebhooks = lazy(() => import("./pages/docs/Webhooks"));
const DocsAgentIntegration = lazy(() => import("./pages/docs/AgentIntegration"));
const DocsFaq = lazy(() => import("./pages/docs/DocsFaq"));
const ProductExport = lazy(() => import("./pages/features/ProductExport"));
const AppDetection = lazy(() => import("./pages/features/AppDetection"));
const ThemeDetection = lazy(() => import("./pages/features/ThemeDetection"));
const PageSpeedAudit = lazy(() => import("./pages/features/PageSpeedAudit"));
const StoreMonitoring = lazy(() => import("./pages/features/StoreMonitoring"));
const SignIn = lazy(() => import("./pages/SignIn"));
const UseCases = lazy(() => import("./pages/use-cases"));
const CompetitorResearch = lazy(() => import("./pages/use-cases/CompetitorResearch"));
const StoreMigration = lazy(() => import("./pages/use-cases/StoreMigration"));
const MarketResearch = lazy(() => import("./pages/use-cases/MarketResearch"));
const SupplierDiscovery = lazy(() => import("./pages/use-cases/SupplierDiscovery"));
const AgencyTools = lazy(() => import("./pages/use-cases/AgencyTools"));
const DeveloperApi = lazy(() => import("./pages/use-cases/DeveloperApi"));

type RouteDefinition = {
  path: string;
  title: string;
  component: LazyExoticComponent<ComponentType>;
  index?: boolean;
};

const featureRoutes: RouteDefinition[] = [
  { path: "/features/product-export", title: "Product Export | ShopSniffer", component: ProductExport },
  { path: "/features/app-detection", title: "App Detection | ShopSniffer", component: AppDetection },
  { path: "/features/theme-detection", title: "Theme Detection | ShopSniffer", component: ThemeDetection },
  { path: "/features/pagespeed-audit", title: "PageSpeed Audit | ShopSniffer", component: PageSpeedAudit },
  { path: "/features/store-monitoring", title: "Store Monitoring | ShopSniffer", component: StoreMonitoring },
];

const useCaseRoutes: RouteDefinition[] = [
  { path: "/use-cases", title: "Shopify Store Analysis Use Cases | ShopSniffer", component: UseCases },
  { path: "/use-cases/competitor-research", title: "Competitor Research | ShopSniffer", component: CompetitorResearch },
  { path: "/use-cases/store-migration", title: "Store Migration | ShopSniffer", component: StoreMigration },
  { path: "/use-cases/market-research", title: "Market Research | ShopSniffer", component: MarketResearch },
  { path: "/use-cases/supplier-discovery", title: "Supplier Discovery | ShopSniffer", component: SupplierDiscovery },
  { path: "/use-cases/agency-tools", title: "Agency Tools | ShopSniffer", component: AgencyTools },
  { path: "/use-cases/developer-api", title: "Developer API | ShopSniffer", component: DeveloperApi },
];

const docsRoutes: RouteDefinition[] = [
  { path: "", title: "API Documentation & Guides | ShopSniffer", component: DocsOverview, index: true },
  { path: "how-it-works", title: "How It Works | ShopSniffer Docs", component: DocsHowItWorks },
  { path: "store-reports", title: "Store Reports | ShopSniffer Docs", component: DocsStoreReports },
  { path: "product-export", title: "Product Export | ShopSniffer Docs", component: DocsProductExportPage },
  { path: "downloads", title: "Downloads | ShopSniffer Docs", component: DocsDownloads },
  { path: "apps-themes", title: "Apps & Themes | ShopSniffer Docs", component: DocsAppsThemes },
  { path: "pagespeed", title: "PageSpeed Audit | ShopSniffer Docs", component: DocsPageSpeed },
  { path: "monitoring", title: "Store Monitoring | ShopSniffer Docs", component: DocsMonitoring },
  { path: "free-export", title: "Free Export | ShopSniffer Docs", component: DocsFreeExport },
  { path: "buying-a-report", title: "Buying a Report | ShopSniffer Docs", component: DocsBuyingReport },
  { path: "authentication", title: "Authentication | ShopSniffer Docs", component: DocsAuthentication },
  { path: "api-reference", title: "API Reference | ShopSniffer Docs", component: DocsApiReference },
  { path: "x402-payment", title: "x402 Payment | ShopSniffer Docs", component: DocsX402Payment },
  { path: "webhooks", title: "Webhooks | ShopSniffer Docs", component: DocsWebhooks },
  { path: "agent-integration", title: "AI Agent Integration | ShopSniffer Docs", component: DocsAgentIntegration },
  { path: "faq", title: "FAQ | ShopSniffer Docs", component: DocsFaq },
];

const staticRoutes: RouteDefinition[] = [
  { path: "/", title: "ShopSniffer | Analyze, Export & Monitor Any Shopify Store", component: Home },
  { path: "/free-export", title: "Free Shopify Store Export Tool | ShopSniffer", component: FreeExport },
  { path: "/report/:id", title: "", component: Report },
  { path: "/dashboard", title: "Dashboard | ShopSniffer", component: Dashboard },
  { path: "/dashboard/store/:slug", title: "", component: StoreDetail },
  { path: "/dashboard/store/:slug/compare", title: "", component: StoreCompare },
  { path: "/blog", title: "Shopify Guides & Tutorials | ShopSniffer Blog", component: Blog },
  { path: "/blog/:slug", title: "", component: BlogPost },
  { path: "/privacy", title: "Privacy Policy | ShopSniffer", component: Privacy },
  { path: "/tos", title: "Terms of Service | ShopSniffer", component: TOS },
  { path: "/stores", title: "Shopify Store Directory | ShopSniffer", component: StoreDirectory },
  { path: "/stores/:slug", title: "", component: StorePage },
  { path: "/payment/complete", title: "", component: PaymentComplete },
  { path: "/support", title: "Support | ShopSniffer", component: Support },
  { path: "/admin", title: "Admin | ShopSniffer", component: Admin },
];

const PAGE_TITLES = Object.fromEntries(
  [...staticRoutes, ...featureRoutes, ...useCaseRoutes]
    .filter((route) => route.title)
    .map((route) => [route.path, route.title]),
);

for (const route of docsRoutes) {
  const path = route.path ? `/docs/${route.path}` : "/docs";
  PAGE_TITLES[path] = route.title;
}

function CacheClearer() {
  const { data: session } = useSession();
  const userId = session?.user?.id ?? null;
  const prevRef = useRef(userId);
  useEffect(() => {
    if (prevRef.current && prevRef.current !== userId) {
      clearCache();
    }
    prevRef.current = userId;
  }, [userId]);
  return null;
}

function PageTitle() {
  const { pathname } = useLocation();

  useEffect(() => {
    const title = PAGE_TITLES[pathname];
    if (title) {
      document.title = title;
    }
  }, [pathname]);

  return null;
}

function FingerprintIQTracker() {
  const { pathname } = useLocation();

  useEffect(() => {
    const apiKey = import.meta.env.VITE_FINGERPRINTIQ_PUBLIC_KEY;
    if (!apiKey) return;

    const surface = pathname.startsWith("/dashboard")
      ? "dashboard"
      : pathname.startsWith("/admin")
        ? "admin"
        : pathname.startsWith("/report")
          ? "report"
          : "public";

    const client = new FingerprintIQ({
      apiKey,
      detectWallets: false,
      cache: { ttl: 3600, storage: "localStorage" },
    });

    void client.identify({
      tag: { app: "shopsniffer", surface, path: pathname },
    }).catch(() => {});
  }, [pathname]);

  return null;
}

function RouteLoader() {
  return (
    <div className="container mx-auto px-4 py-12">
      <Loading text="Loading page..." />
    </div>
  );
}

function renderLazyElement(Component: RouteDefinition["component"]) {
  return <Component />;
}

export default function App() {
  return (
    <div className="flex min-h-screen flex-col bg-background">
      <CacheClearer />
      <PageTitle />
      <FingerprintIQTracker />
      <Header />
      <main className="flex-1">
        <ErrorBoundary>
          <Suspense fallback={<RouteLoader />}>
            <Routes>
              <Route path="/sign-in" element={<SignIn />} />
              <Route path="/sign-up" element={<SignIn />} />
              {staticRoutes.map((route) => (
                <Route key={route.path} path={route.path} element={renderLazyElement(route.component)} />
              ))}

              {featureRoutes.map((route) => (
                <Route key={route.path} path={route.path} element={renderLazyElement(route.component)} />
              ))}

              {useCaseRoutes.map((route) => (
                <Route key={route.path} path={route.path} element={renderLazyElement(route.component)} />
              ))}

              <Route path="/docs" element={<DocsLayout />}>
                {docsRoutes.map((route) => (
                  <Route
                    key={route.path || "index"}
                    index={route.index}
                    path={route.index ? undefined : route.path}
                    element={renderLazyElement(route.component)}
                  />
                ))}
              </Route>
            </Routes>
          </Suspense>
        </ErrorBoundary>
      </main>
      <Footer />
      <AdminToolbar />
    </div>
  );
}
